import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { type ApAgentAction } from "@/accounts-payable/types";
import { formatDollars } from "@/accounts-payable/utils";

/**
 * On a pending PO whose lines carry new pricebook items: lists the ones its
 * approval also creates, and shows the API's reason when it would refuse the
 * approval (e.g. one of the items was dismissed).
 */
export const NewPricebookItemsNote = ({
  items,
  blockedReason,
  blockedReasonId,
}: {
  /** The PO's pending `create_pricebook_item` prerequisites. */
  items: ApAgentAction[];
  /** The API's `approval_blocked_reason` for the PO. */
  blockedReason?: string | null;
  /** Id for the reason, which the disabled Approve button points to. */
  blockedReasonId: string;
}) => {
  const count = items.length;

  return (
    <Stack
      align="start"
      gap={2}
      className={cn("w-full", "rounded-md border px-3 py-3", {
        "border-danger-line": blockedReason,
        "border-line": !blockedReason,
      })}
    >
      {count > 0 && (
        <>
          {/* A list label, not a sentence: the approve button already says
              approving the PO creates these, and counts the same items. */}
          <Text variant="label">New pricebook items ({count})</Text>
          <Stack className="w-full" align="start" gap={1}>
            {items.map((item) => (
              <Stack
                className="w-full"
                align="start"
                key={item.id}
                direction="row"
                justify="between"
                gap={4}
              >
                <Stack align="start" gap={0} className="min-w-0">
                  <Text>{item.payload?.name}</Text>
                  <Text variant="caption" tone="muted">
                    {item.payload?.code}
                  </Text>
                </Stack>
                <Text className="flex-none tabular-nums">
                  {formatDollars(item.payload?.cost)}
                </Text>
              </Stack>
            ))}
          </Stack>
        </>
      )}
      {blockedReason && (
        <Text id={blockedReasonId} variant="caption" tone="destructive">
          {blockedReason}
        </Text>
      )}
    </Stack>
  );
};
